import memoize from 'lodash.memoize';
import { createSelector, createStructuredSelector } from 'reselect';
import * as technical from './iiif/technical';
import * as descriptive from './iiif/descriptive';
import * as linking from './iiif/linking';
import * as structural from './iiif/structural';
import { getAllAnnotationLists, getAllAnnotations, getAllExternalResources, getAllImages, getAllLayers, getAllServices } from '../all';
import { isImageService } from '../../constants/services';

var canvas = memoize(function (selector) {
  /**************************************************
   * Technical properties
   *
   * - getId
   * - getType
   * - getViewingHint
   * - getHeight
   * - getWidth
   **************************************************/
  var getId = createSelector(selector, technical.getId);

  var getType = createSelector(selector, technical.getType);

  var getViewingHint = createSelector(selector, technical.getWhitelistedViewingHint(['non-paged', 'facing-pages']));

  var getHeight = createSelector(selector, technical.getHeight);

  var getWidth = createSelector(selector, technical.getWidth);

  /**************************************************
   * Descriptive properties
   *
   * - getLabel
   * - getMetadata
   * - getDescription
   * - getThumbnail
   * - getAttribution
   * - getLicense
   * - getLogo
   **************************************************/
  var getLabel = createSelector(selector, descriptive.getLabel);

  var getDescription = createSelector(selector, descriptive.getDescription);

  var getMetadata = createSelector(selector, descriptive.getMetadata);

  var getAttribution = createSelector(selector, descriptive.getAttribution);

  var getLogo = createSelector(selector, descriptive.getLogo);

  var getLicense = createSelector(selector, descriptive.getLicense);

  var getThumbnailId = createSelector(selector, descriptive.getThumbnailId);
  var getThumbnail = createSelector(getThumbnailId, getAllImages, function (thumbnailId, allImages) {
    return allImages[thumbnailId] || thumbnailId;
  });

  /**************************************************
   * Linking properties
   *
   * - getSeeAlso
   * - getService
   * - getRelated
   * - getRendering
   * - getWithin
   **************************************************/
  var getSeeAlsoIds = createSelector(selector, linking.getSeeAlso);
  var getSeeAlso = createSelector(getSeeAlsoIds, getAllExternalResources, function (seeAlsoIds, allExternalResources) {
    return seeAlsoIds.map(function (seeAlsoId) {
      return allExternalResources[seeAlsoId] || {
        '@id': seeAlsoId,
        label: [{ '@value': 'unknown', '@language': '@none' }]
      };
    });
  });

  var getServiceIds = createSelector(selector, linking.getService);
  var getService = createSelector(getServiceIds, getAllServices, function (serviceIds, allServices) {
    return serviceIds.map(function (serviceId) {
      return allServices[serviceId];
    });
  });

  var getRelatedIds = createSelector(selector, linking.getRelated);
  var getRelated = createSelector(getRelatedIds, getAllExternalResources, function (relatedIds, allExternalResources) {
    return relatedIds.map(function (relatedId) {
      return allExternalResources[relatedId];
    });
  });

  var getRenderingIds = createSelector(selector, linking.getRendering);
  var getRendering = createSelector(getRenderingIds, getAllExternalResources, function (renderingIds, allExternalResources) {
    return renderingIds.map(function (renderingId) {
      return allExternalResources[renderingId];
    });
  });

  var getWithinIds = createSelector(selector, linking.getWithin);
  var getWithin = createSelector(getWithinIds, getAllLayers, getAllExternalResources, function (withinIds, allLayers, allExternalResources) {
    return withinIds.map(function (within) {
      return allLayers[within.id] || allExternalResources[within.id];
    }).filter(function (e) {
      return e;
    });
  });

  /**************************************************
   * Structural properties
   *
   * - getOtherContent
   * - getImages
   **************************************************/
  var getOtherContentIds = createSelector(selector, structural.getOtherContent);
  var getOtherContent = createSelector(getOtherContentIds, getAllExternalResources, getAllAnnotationLists, function (otherContentIds, allExternalResources, allAnnotationLists) {
    return otherContentIds.map(function (otherContentId) {
      return allAnnotationLists[otherContentId] || allExternalResources[otherContentId] || {
        '@id': otherContentId,
        label: [{ '@value': 'unknown', '@language': '@none' }]
      };
    });
  });

  var getImageIds = createSelector(selector, structural.getImages);
  var getImages = createSelector(getImageIds, getAllAnnotations, function (imageIds, allImages) {
    return imageIds.map(function (imageId) {
      return allImages[imageId];
    });
  });

  /**************************************************
   * Algorithms
   *
   * - getImageService
   **************************************************/
  var getImageService = createSelector(getImages, getAllServices, getAllImages, function (images, allServices, allImages) {
    return (
      // The basic path is images[x].resource.service
      images.reduce(function (result, image) {
        if (result) return result;
        var resource = image.resource.schema === 'imageResource' ? allImages[image.resource.id] : { service: [] };

        return (resource.service || []).reduce(function (innerResult, serviceId) {
          if (innerResult) return innerResult;
          var service = allServices[serviceId];
          if (isImageService(service.profile)) {
            return service;
          }
        }, null);
      }, null)
    );
  });

  return {
    // Technical
    getId: getId,
    getType: getType,
    getViewingHint: getViewingHint,
    getHeight: getHeight,
    getWidth: getWidth,
    // Descriptive
    getLabel: getLabel,
    getMetadata: getMetadata,
    getDescription: getDescription,
    getThumbnailId: getThumbnailId,
    getThumbnail: getThumbnail,
    getAttribution: getAttribution,
    getLicense: getLicense,
    getLogo: getLogo,
    // Linking
    getWithinIds: getWithinIds,
    getWithin: getWithin,
    getRenderingIds: getRenderingIds,
    getRendering: getRendering,
    getRelatedIds: getRelatedIds,
    getRelated: getRelated,
    getServiceIds: getServiceIds,
    getService: getService,
    getSeeAlsoIds: getSeeAlsoIds,
    getSeeAlso: getSeeAlso,
    // Structural
    getOtherContentIds: getOtherContentIds,
    getOtherContent: getOtherContent,
    getImageIds: getImageIds,
    getImages: getImages,
    // Algorithms.
    getImageService: getImageService
  };
});

export default canvas;